import { useReducedMotion } from 'motion/react';
import { useEffect, useRef } from 'react';
import { MessageCircle } from 'lucide-react';
import { AnimatedLayoutDashboard } from './AnimatedLayoutDashboard';
import { BellIcon } from './lucide-animated/bell';
import { ChevronLeftIcon } from './lucide-animated/chevron-left';
import { CreditCardIcon } from './lucide-animated/credit-card';
import { HeartIcon } from './lucide-animated/heart';
import { LogoutIcon } from './lucide-animated/logout';
import { PanelLeftOpenIcon } from './lucide-animated/panel-left-open';
import { ReceiptTextIcon } from './lucide-animated/receipt-text';
import { SettingsIcon } from './lucide-animated/settings';
import { WalletIcon } from './lucide-animated/wallet';

const icons = {
  dashboard: AnimatedLayoutDashboard,
  accounts: CreditCardIcon,
  budget: WalletIcon,
  household: HeartIcon,
  transactions: ReceiptTextIcon,
  settings: SettingsIcon,
  logout: LogoutIcon,
  collapse: ChevronLeftIcon,
  expand: PanelLeftOpenIcon,
  notifications: BellIcon,
};

export type SidebarIconName = keyof typeof icons | 'assistant';

// The upstream imperative API lets the entire control trigger its icon.
export function SidebarIcon({
  name,
  className,
  size = 20,
}: {
  name: SidebarIconName;
  className?: string;
  size?: number;
}) {
  const container = useRef<HTMLDivElement>(null);
  const animation = useRef<{
    startAnimation: () => void;
    stopAnimation: () => void;
  }>(null);
  const reducedMotion = useReducedMotion();
  const Icon = name === 'assistant' ? null : icons[name];

  useEffect(() => {
    const control = container.current?.closest('a, button, [role="menuitem"]');
    if (!control) return;

    const update = () => {
      if (!reducedMotion && control.matches(':hover, :focus-visible')) {
        animation.current?.startAnimation();
      } else {
        animation.current?.stopAnimation();
      }
    };

    control.addEventListener('mouseenter', update);
    control.addEventListener('mouseleave', update);
    control.addEventListener('focus', update);
    control.addEventListener('blur', update);
    update();
    return () => {
      control.removeEventListener('mouseenter', update);
      control.removeEventListener('mouseleave', update);
      control.removeEventListener('focus', update);
      control.removeEventListener('blur', update);
    };
  }, [name, reducedMotion]);

  return (
    <div ref={container} className={className} aria-hidden="true">
      {Icon ? (
        <Icon ref={animation} size={size} />
      ) : (
        <MessageCircle size={size} />
      )}
    </div>
  );
}
